import { forwardRef, memo, useMemo, useCallback } from "react";
import { StarIcon as Star, HeartIcon as Heart, FlameIcon as Flame, ShoppingBagIcon as ShoppingBag, ZapIcon as Zap } from "./icons";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import OptimizedImage from "@/components/OptimizedImage";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { useQuickBuy } from "@/contexts/QuickBuyContext";

export interface Product {
  id: number | string;
  name: string;
  price: string | number;
  offer_price?: number | null;
  description?: string | null;
  rating?: number;
  image?: string | null;
  category?: string;
  category_id?: string | null;
  seo_slug?: string | null;
  stock?: number;
}

interface ProductCardProps {
  product: Product;
  isWishlisted?: boolean;
  onToggleWishlist?: (productId: string | number) => void;
}

const ProductCard = memo(forwardRef<HTMLDivElement, ProductCardProps>(({ product, isWishlisted, onToggleWishlist }, ref) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme } = useSiteTheme();
  const { openQuickBuy } = useQuickBuy();

  const numericPrice = typeof product.price === "number"
    ? product.price
    : parseInt(String(product.price).replace(/[^\d]/g, ""), 10) || 0;

  const effectivePrice = product.offer_price ?? numericPrice;
  const displayPrice = `৳${effectivePrice.toLocaleString()}`;
  const hasOffer = product.offer_price != null && product.offer_price < numericPrice;
  const discountPercent = hasOffer ? Math.round(((numericPrice - product.offer_price!) / numericPrice) * 100) : 0;
  const isOutOfStock = product.stock === 0;
  const rating = product.rating ?? 5;
  const stock = product.stock ?? -1;
  const showLowStock = !isOutOfStock && stock >= 0 && stock <= 10;

  const handleWishlist = useCallback((e: React.MouseEvent) => { e.stopPropagation(); onToggleWishlist?.(product.id); }, [onToggleWishlist, product.id]);

  const goToProduct = useCallback(() => {
    const previewDesign = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("preview_design")
      : null;
    const suffix = previewDesign ? `?preview_design=${previewDesign}` : "";
    navigate(`/product/${product.seo_slug || product.id}${suffix}`);
  }, [navigate, product.seo_slug, product.id]);

  const handleBuyNow = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    openQuickBuy(product, "buy");
  }, [openQuickBuy, product]);

  const handleAddToCart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    openQuickBuy(product, "cart");
  }, [openQuickBuy, product]);

  const cardStyleMap: Record<string, string> = {
    default: "rounded-3xl",
    rounded: "rounded-3xl",
    flat: "rounded-none",
    bordered: "rounded-2xl",
    glass: "rounded-3xl backdrop-blur-sm",
  };

  const cardClass = cardStyleMap[theme.cardStyle] || "rounded-3xl";
  const isGlass = theme.cardStyle === "glass";
  // Forced B&W palette across all storefront cards
  const goldenColor = "#000000";
  const starFilled = "#000000";
  const starEmpty = "#cccccc";

  // Static 5-star array
  const stars = useMemo(() => Array.from({ length: 5 }), []);

  return (
    <div
      ref={ref}
      className={`group overflow-hidden cursor-pointer relative ${cardClass} ${isOutOfStock ? "opacity-75" : ""}`}
      style={{
        backgroundColor: isGlass ? `hsl(${theme.cardBg} / 0.7)` : `hsl(${theme.cardBg})`,
        borderWidth: "1px",
        borderColor: `hsl(${theme.borderColor})`,
        borderRadius: theme.cardStyle === "default" ? "24px" : undefined,
        contain: 'layout style paint',
      }}
      onClick={goToProduct}
    >
      {/* Fixed aspect ratio container — prevents CLS */}
      <div className="relative overflow-hidden aspect-[3/4]" style={{ backgroundColor: `hsl(${theme.sectionAltBg})` }}>
        <img
          src={product.image || "/placeholder.svg"}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-top"
        />

        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-black/70 text-white font-heading text-xs sm:text-sm tracking-widest uppercase px-4 py-2 rounded-lg backdrop-blur-sm">
              {t("out_of_stock")}
            </div>
          </div>
        )}

        <div className="absolute top-1.5 left-1.5 md:top-2 md:left-2 flex flex-col gap-1 z-20">
          {hasOffer && !isOutOfStock && (
            <span className="text-[9px] md:text-[10px] font-body font-bold px-1.5 md:px-2 py-0.5 rounded-full" style={{ backgroundColor: `hsl(${theme.badgeBg})`, color: `hsl(${theme.badgeText})` }}>
              -{discountPercent}%
            </span>
          )}
          {showLowStock && (
            <span className="text-[9px] md:text-[10px] font-body font-bold px-1.5 md:px-2 py-0.5 rounded-full flex items-center gap-0.5" style={{ backgroundColor: `hsl(${theme.badgeBg})`, color: `hsl(${theme.badgeText})` }}>
              <Flame size={9} /> Very Low Stock
            </span>
          )}
        </div>

        {onToggleWishlist && (
          <button onClick={handleWishlist} className="absolute top-1.5 right-1.5 md:top-2 md:right-2 p-1.5 rounded-full backdrop-blur-sm md:opacity-0 md:group-hover:opacity-100 z-20 transition-opacity" style={{ backgroundColor: `hsl(${theme.cardBg} / 0.6)`, color: `hsl(${theme.mutedText})` }}>
            <Heart size={14} className={isWishlisted ? "fill-destructive text-destructive" : ""} />
          </button>
        )}

        {!isOutOfStock && (
          <div className="absolute bottom-2 left-2 right-2 translate-y-[calc(100%+8px)] group-hover:translate-y-0 transition-transform duration-300 ease-out hidden md:flex gap-1.5">
            <button
              onClick={handleBuyNow}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-body font-semibold tracking-wider uppercase rounded-2xl"
              style={{ backgroundColor: goldenColor, color: "#fff" }}
            >
              <Zap size={12} /> Buy Now
            </button>
            <button
              onClick={handleAddToCart}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-body font-semibold tracking-wider uppercase rounded-2xl"
              style={{ backgroundColor: `hsl(${theme.cardBg})`, color: `hsl(${theme.cardText})`, border: `1px solid hsl(${theme.borderColor})` }}
            >
              <ShoppingBag size={12} /> Add to Cart
            </button>
          </div>
        )}
      </div>

      <div className="px-3 pt-3 pb-2 sm:p-3 md:p-4">
        <h3 className="font-heading text-[11px] sm:text-sm md:text-base mb-1 line-clamp-2 leading-snug" style={{ color: `hsl(${theme.cardText})` }}>{product.name}</h3>
        <div className="flex items-center gap-0.5 mb-1.5">
          {stars.map((_, i) => (
            <Star key={i} size={10} style={{ color: i < rating ? starFilled : starEmpty, fill: i < rating ? starFilled : "none" }} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <p className="font-price font-semibold text-xl sm:text-base md:text-lg leading-none" style={{ color: `hsl(${theme.cardText})` }}>{displayPrice}</p>
          {hasOffer && <p className="font-price text-xs sm:text-sm line-through" style={{ color: `hsl(${theme.mutedText})` }}>৳{numericPrice.toLocaleString()}</p>}
        </div>
      </div>

      {!isOutOfStock && (
        <div className="grid grid-cols-2 md:hidden gap-1.5 px-2.5 pb-2.5">
          <button
            onClick={handleBuyNow}
            className="h-8 flex items-center justify-center gap-1 text-[9px] font-body font-semibold tracking-wider uppercase rounded-lg whitespace-nowrap"
            style={{ backgroundColor: goldenColor, color: "#fff" }}
          >
            <Zap size={9} className="shrink-0" /> Buy Now
          </button>
          <button
            onClick={handleAddToCart}
            className="h-8 flex items-center justify-center gap-1 text-[9px] font-body font-semibold tracking-wider uppercase rounded-lg whitespace-nowrap"
            style={{ backgroundColor: `hsl(${theme.cardBg})`, color: `hsl(${theme.cardText})`, border: `1px solid hsl(${theme.borderColor})` }}
          >
            <ShoppingBag size={9} className="shrink-0" /> Cart
          </button>
        </div>
      )}
    </div>
  );
}));

ProductCard.displayName = "ProductCard";

export default ProductCard;
